import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { formatCurrency } from '../../lib/format';
import {
  inkoopExclUitIncl,
  isInterneReparatie,
  reparatiePrijs,
  sendReparatieQuote,
  updateReparatiePurchase,
  type ReparatieOpdracht,
} from '../../lib/reparatie';

interface Props {
  opdracht: ReparatieOpdracht;
  onClose: () => void;
  onUpdated: () => void;
}

export function ReparatieInkoopDialog({ opdracht, onClose, onUpdated }: Props) {
  const isEdit = opdracht.inkoopBedrag != null;
  const isIntern = isInterneReparatie(opdracht);
  const wasQuoteSent = opdracht.status === 'Offerte naar klant';
  const [inkoop, setInkoop] = useState<string>(
    opdracht.inkoopBedrag != null
      ? String(inkoopExclUitIncl(opdracht.inkoopBedrag))
      : ''
  );
  // Bij intern is er geen klantprijs, dus geen marge. De server forceert 0 ook
  // als hier iets anders vandaan zou komen; dit houdt het scherm eerlijk.
  const [marge, setMarge] = useState<string>(
    isIntern ? '0' : opdracht.margePercentage != null ? String(opdracht.margePercentage) : '50'
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'edit' | 'quote-prompt' | 'sent'>('edit');

  const inkoopNum = parseFloat(inkoop.replace(',', '.'));
  const margeNum = parseFloat(marge.replace(',', '.'));
  // Reparateurs offreren exclusief BTW (Pazirik doet dat consequent); de
  // prijsregel zelf staat in lib/reparatie.ts, zodat het voorbeeld op dit
  // scherm niet kan uiteenlopen met wat de server opslaat.
  const prijs = useMemo(() => reparatiePrijs(inkoopNum, margeNum), [inkoopNum, margeNum]);
  const inkoopInclNum = prijs?.inkoopIncl ?? NaN;
  const verkoopIncl = prijs?.verkoopIncl ?? null;
  const verkoopExcl = prijs?.verkoopExcl ?? null;

  const canSave = step === 'edit' && !busy && verkoopIncl != null;

  const handleSave = async () => {
    if (verkoopIncl == null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await updateReparatiePurchase({
        opdrachtId: opdracht.id,
        inkoopBedrag: inkoopInclNum,
        margePercentage: margeNum,
      });
      if (!res.success) {
        setError(res.message || 'Update mislukt');
        setBusy(false);
        return;
      }
      // Interne opdracht: er is niets om aan een klant voor te leggen, dus
      // sla de "wil je dit versturen?"-stap over in plaats van hem te tonen
      // met een uitgeschakelde knop. Geen klantpad betekent geen klantvraag.
      if (isIntern) {
        onUpdated();
        return;
      }
      setStep('quote-prompt');
      setBusy(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update mislukt');
      setBusy(false);
    }
  };

  const handleSendQuote = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await sendReparatieQuote(opdracht.id);
      if (!res.success) {
        setError(res.message || 'Offerte versturen mislukt');
        setBusy(false);
        return;
      }
      setStep('sent');
      setBusy(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Offerte versturen mislukt');
      setBusy(false);
    }
  };

  const handleDone = () => onUpdated();

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">
            {isEdit ? 'Prijs aanpassen' : 'Inkoop invoeren'}
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            {opdracht.opdrachtNummer}
            {opdracht.reparateurNaam ? ` — ${opdracht.reparateurNaam}` : ''}
          </p>
          {wasQuoteSent && step === 'edit' && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2">
              Klant heeft al een offerte ontvangen. Na opslaan staat de opdracht weer op
              "Offerte ontvangen" — verstuur dan opnieuw zodat de klant de nieuwe prijs ziet.
            </p>
          )}
        </div>

        {step === 'edit' && (
          <>
            <div className="px-6 py-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {isIntern ? 'Kostprijs reparateur (€, excl. BTW)' : 'Prijs reparateur (€, excl. BTW)'}
                  <span className="text-slate-400 font-normal"> — zoals hij offreert</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                  value={inkoop}
                  onChange={(e) => setInkoop(e.target.value)}
                  disabled={busy}
                  placeholder="80.00"
                />
              </div>
              {!isIntern && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Marge (%)
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                    value={marge}
                    onChange={(e) => setMarge(e.target.value)}
                    disabled={busy}
                    placeholder="50"
                  />
                </div>
              )}
              {isIntern ? (
                <div className="bg-violet-50 border border-violet-200 rounded p-3">
                  <p className="text-sm font-medium text-violet-900">
                    Interne reparatie — dit is onze eigen kostenpost
                  </p>
                  <p className="text-xs text-violet-800 mt-1">
                    Er gaat geen offerte naar de klant en er komt geen regel op haar factuur.
                    Vul het echte bedrag in, dit is de enige plek waar de schade zichtbaar wordt.
                  </p>
                </div>
              ) : (
                <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-1">
                  {/* De omrekening zichtbaar: dit ging eerder stil mis. */}
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Onze kosten incl. BTW</span>
                    <span className="text-slate-700">
                      {Number.isFinite(inkoopInclNum) && inkoopInclNum > 0
                        ? formatCurrency(inkoopInclNum)
                        : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Klant betaalt excl. BTW</span>
                    <span className="text-slate-700">
                      {verkoopExcl != null ? formatCurrency(verkoopExcl) : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm pt-1 border-t border-slate-200">
                    <span className="text-slate-500">Klant betaalt incl. BTW</span>
                    <span className="font-medium text-slate-900">
                      {verkoopIncl != null ? formatCurrency(verkoopIncl) : '—'}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400 pt-0.5">
                    De marge rekent over de inkoop; bij {Number.isFinite(margeNum) ? margeNum : 0}% is
                    dat {Number.isFinite(margeNum) ? (1 + margeNum / 100).toFixed(2).replace('.', ',') : '—'}x
                    de prijs van de reparateur.
                  </p>
                </div>
              )}
              {error && <p className="text-xs text-red-600">{error}</p>}
            </div>
            <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!canSave}
                className={clsx(
                  'text-sm font-medium px-3 py-1.5 rounded',
                  canSave
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-slate-200 text-slate-500 cursor-not-allowed'
                )}
              >
                {busy ? 'Opslaan…' : 'Opslaan'}
              </button>
            </div>
          </>
        )}

        {step === 'quote-prompt' && (
          <>
            <div className="px-6 py-5 space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                <p className="text-sm font-medium text-emerald-900">Inkoop opgeslagen</p>
                <p className="text-xs text-emerald-700 mt-1">
                  Klant betaalt {verkoopIncl != null ? formatCurrency(verkoopIncl) : '—'} incl. BTW
                  {verkoopExcl != null ? ` (${formatCurrency(verkoopExcl)} excl.)` : ''}
                </p>
              </div>
              <p className="text-sm text-slate-700">Wil je het voorstel direct naar de klant sturen?</p>
              {error && <p className="text-xs text-red-600">{error}</p>}
            </div>
            <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleDone}
                disabled={busy}
                className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
              >
                Later
              </button>
              <button
                type="button"
                onClick={handleSendQuote}
                disabled={busy}
                className={clsx(
                  'text-sm font-medium px-3 py-1.5 rounded',
                  busy
                    ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                )}
              >
                {busy ? 'Versturen…' : 'Verstuur voorstel'}
              </button>
            </div>
          </>
        )}

        {step === 'sent' && (
          <div className="px-6 py-6 space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
              <p className="text-sm font-medium text-emerald-900">Voorstel verstuurd naar klant</p>
              <p className="text-xs text-emerald-700 mt-1">
                Klant ontvangt een mail met de offerte en een akkoord-link.
              </p>
            </div>
            <div className="flex justify-end pt-1">
              <button
                type="button"
                onClick={handleDone}
                className="text-sm font-medium px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Sluiten
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
